import React, { FC, memo } from 'react';
import {
  ArrowDownIcon,
  CheckCircleOutlineIcon,
  CircleCloseIcon,
  CloseIcon,
  ForbiddenIcon,
  LockIcon,
  TableButtonsIcon,
  ViewIcon,
} from '@src/svg';
import TruncatedText from '@component/TruncatedText/TruncatedText';
import CustomDropdown from '@uikit/CustomDropdown/CustomDropdown';
import { StatusWrapper } from '@component/StatusWrapper/StatusWrapper';
import { getNewAbilities, handleImgError } from '@src/utils';
import { normalizeOnlineStatus } from '@utils/helpers';
import { FALLBACK_IMAGE, IMAGE_STORAGE_URL } from '@src/utils/constants';
import { Column } from '@uikit/Tables/types';
import { idType } from '@src/types';
import {
  getIsPermissionToggleEnabled,
  getPermissionAbilityId,
} from '../utils/permissions';
import {
  buildButtonsDropdownOptions,
  buildStatusDropdownOptions,
} from '../utils/dropdownOptions';
import { ModalType } from '@uikit/Modal/modals/constants';
import { useAppSelector } from '@store/hooks';
import { selectUsers } from '@store/slices/users/usersSlice';
import { CustomTableProps } from '@uikit/Tables/TableDynamic/constants';

interface TableCellProps {
  col: Column;
  row: any;
  index: number;
  abilities: string[];
  modalName?: string;
  tableName?: string;
  openModal?: (name: string, id: idType) => void;
  onEditClick?: (id: idType) => void;
  onAuditClick?: (id: idType, type: ModalType, name?: string) => void;
  onImageClick?: (image: string, uploadedBy: string) => void;
  onFieldClick?: (row: any, field: string) => void;
  handleSelectedRow?: (id: idType) => void;
  handleDeleteRow?: (id: idType) => void;
  statusSetter?: (id: idType, status: boolean) => void;
  onUpdate?: CustomTableProps['onUpdate'];
  setSelectedItem: (id: idType) => void;
}

const TableCell: FC<TableCellProps> = memo(
  ({
    col,
    row,
    index,
    abilities,
    modalName,
    tableName,
    openModal,
    onEditClick,
    onAuditClick,
    onImageClick,
    onFieldClick,
    handleSelectedRow,
    handleDeleteRow,
    statusSetter,
    onUpdate,
    setSelectedItem,
  }) => {
    const isPermissionToggleEnabled = getIsPermissionToggleEnabled();
    const { newPermissions } = useAppSelector(selectUsers);
    const auditLogAbilities = getNewAbilities(
      'modificationHistory',
      newPermissions
    );

    const newAuditAbilities = auditLogAbilities.abilities.some((el) =>
      ['Edit', 'Read'].includes(el)
    );

    const renderStringField = () => {
      const abilityId = getPermissionAbilityId(col.field);
      const isPermissionField =
        col.type === 'boolean' &&
        abilityId !== undefined &&
        Object.prototype.hasOwnProperty.call(row, col.field);

      if (col.type === 'clickableString') {
        return (
          <span
            className="table-clickable-field"
            onClick={() => onFieldClick?.(row, col.field)}
          >
            <TruncatedText text={row[col.field]} />
          </span>
        );
      }

      if (isPermissionField) {
        return (
          <button
            type="button"
            className={`table-permission-toggle ${
              row[col.field] ? 'permission-granted' : 'permission-denied'
            }`}
            aria-label={`${row[col.field] ? 'Disable' : 'Enable'} ${col.field} permission`}
            onClick={(e) => {
              e.stopPropagation();
              if (!isPermissionToggleEnabled) return;
              onUpdate?.({
                permissionId: index,
                abilityId,
                status: !row[col.field],
              });
            }}
          >
            {!isPermissionToggleEnabled
              ? row[col.field]
              : row[col.field] || <CircleCloseIcon width={16} height={16} />}
          </button>
        );
      }

      const dynamicClassName =
        col.className === 'type_styles__admin' ? col.className : '';
      return (
        <div className={dynamicClassName}>
          <TruncatedText text={row[col.field]} />
        </div>
      );
    };

    if (col.renderCell) {
      return <>{col.renderCell(row)}</>;
    }

    if (col.field === 'viewImage') {
      return (
        <button
          onClick={() => onImageClick?.(row[col.field], row.uploadedBy)}
          disabled
          className={`my-table-button ${!abilities?.includes('Document') ? 'btn_disable' : ''}`}
        >
          <ForbiddenIcon />
        </button>
      );
    }

    if (col.field === 'view') {
      return (
        <div
          onClick={(e) => {
            e.stopPropagation();
            handleSelectedRow?.(row.id);
          }}
          style={{
            display: 'flex',
            justifyContent: 'center',
            cursor: 'pointer',
          }}
        >
          <ViewIcon />
        </div>
      );
    }

    if (col.type === 'checkbox') {
      return row[col.field] ? <CheckCircleOutlineIcon /> : <CircleCloseIcon />;
    }

    if (
      col.type === 'buttons' &&
      col.buttons &&
      Object.values(col.buttons).some(Boolean)
    ) {
      return (
        <CustomDropdown
          button={<TableButtonsIcon />}
          buttonClassName="table-buttons-trigger"
          options={buildButtonsDropdownOptions({
            rowId: row.id,
            buttons: col?.buttons,
            // abilities,
            abilities: newAuditAbilities ? [...abilities, 'Audit'] : abilities,
            modalName,
            tableName,
            openModal,
            onEditClick,
            onAuditClick,
            handleSelectedRow,
            setSelectedItem,
            handleDeleteRow,
          })}
        />
      );
    }

    if (col.type === 'avatar') {
      return (
        <div className="table_avatar">
          {row?.[col.field]?.hasOwnProperty('icon') ? (
            <div className="table_avatar_icon">{row?.[col.field]?.icon}</div>
          ) : (
            <div className="image_wrapper">
              <StatusWrapper
                size={10}
                statusColor={normalizeOnlineStatus(row?.[col.field]?.status)}
              >
                <img
                  alt="table avatar image"
                  src={
                    row?.[col.field]?.image
                      ? `${IMAGE_STORAGE_URL}/${row?.[col.field].image}`
                      : FALLBACK_IMAGE
                  }
                  onError={handleImgError}
                />
              </StatusWrapper>
            </div>
          )}
          <div className="table_avatar_info">
            <div className="table_avatar_fullName">
              <TruncatedText
                text={`${row?.[col.field].firstName} ${row?.[col.field].lastName ?? ''}`}
              />
            </div>
            <div className="table_avatar_relation">
              <h4>{row?.[col.field].permission}</h4>
            </div>
          </div>
        </div>
      );
    }

    if (col.type === 'status') {
      return (
        <div className="table_status">
          {row.isLocked && (
            <span className="lock_icon_wrapper">
              <LockIcon width={16} height={16} color="#FFF" />
            </span>
          )}
          <CustomDropdown
            button={
              <div
                className={`${row?.[col.field] ? 'active' : 'inactive'} status_dropdown`}
              >
                {row?.[col.field] ? 'Active' : 'Inactive'}
                <ArrowDownIcon width={16} height={16} />
              </div>
            }
            options={buildStatusDropdownOptions({
              id: row.id,
              value: row?.[col.field],
              statusSetter,
            })}
          />
          {handleDeleteRow && !row?.isMemberContact && (
            <button
              onClick={(e) => {
                e.stopPropagation();
                handleDeleteRow(row.id);
              }}
              className="close-button"
            >
              <CloseIcon width={16} height={16} color="#FFFFFF" />
            </button>
          )}
        </div>
      );
    }

    return <>{renderStringField()}</>;
  }
);

TableCell.displayName = 'TableCell';
export default TableCell;
